import React, { useEffect, useState, useRef } from 'react';
import taskAPI from '../api/taskAPI';
import UserDashboard from './UserDashboard';
import {
  FaUserPlus, FaTimes, FaLink, FaChevronRight,
  FaCalendarAlt, FaAlignLeft,
  FaUpload, FaFilePdf, FaFileWord, FaFileImage,
  FaFile, FaDownload, FaEye, FaSpinner, FaTrash,
} from 'react-icons/fa';
import { MdAssignment, MdGroups } from 'react-icons/md';

// ── Shared helpers (same across all task managers) ────────────────────────────
const ROLE_COLOR = {
  'Marketing':     'bg-pink-100 text-pink-700',
  'Project Leads': 'bg-indigo-100 text-indigo-700',
  'HR & Design':   'bg-teal-100 text-teal-700',
  'TL':            'bg-blue-100 text-blue-700',
  'Developer':     'bg-green-100 text-green-700',
  'Office':        'bg-gray-100 text-gray-600',
};
const RoleBadge = ({ role }) => (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLOR[role] || 'bg-gray-100 text-gray-600'}`}>{role}</span>
);
const ChainTrail = ({ chain }) => {
  if (!chain?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      {chain.map((entry, i) => (
        <React.Fragment key={i}>
          <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full">{entry}</span>
          {i < chain.length - 1 && <FaChevronRight className="text-purple-300 text-xs" />}
        </React.Fragment>
      ))}
    </div>
  );
};
const getFileType = (file) => {
  if (file.type.startsWith('image/'))  return 'image';
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx')) return 'doc';
  return 'other';
};
const FileIcon = ({ fileType }) => {
  if (fileType === 'image') return <FaFileImage className="text-blue-500 text-xl shrink-0" />;
  if (fileType === 'pdf')   return <FaFilePdf   className="text-red-500 text-xl shrink-0" />;
  if (fileType === 'doc')   return <FaFileWord  className="text-blue-700 text-xl shrink-0" />;
  return <FaFile className="text-gray-500 text-xl shrink-0" />;
};
const formatSize = (b) =>
  b < 1024 ? `${b}B` : b < 1048576 ? `${(b/1024).toFixed(1)}KB` : `${(b/1048576).toFixed(1)}MB`;
const toBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });

// ── FileUploadPanel ───────────────────────────────────────────────────────────
const FileUploadPanel = ({ taskId, existingFiles = [], onUploaded }) => {
  const [pending, setPending]       = useState([]);
  const [uploading, setUploading]   = useState(false);
  const [uploaded, setUploaded]     = useState(existingFiles);
  const [previewUrl, setPreviewUrl] = useState(null);
  const inputRef = useRef(null);
  const addFiles = (fileList) => {
    setPending(prev => [...prev, ...Array.from(fileList).map(f => ({
      file: f, name: f.name, size: f.size, fileType: getFileType(f),
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }))]);
  };
  const handleUpload = async () => {
    if (!taskId) { alert('Save task first.'); return; }
    if (!pending.length) { alert('No files selected.'); return; }
    setUploading(true);
    try {
      const files = await Promise.all(pending.map(async pf => ({ base64: await toBase64(pf.file), name: pf.name, fileType: pf.fileType })));
      const res = await taskAPI.post(`/${taskId}/attachments`, { files });
      setUploaded(res.data.attachments); setPending([]);
      onUploaded?.(res.data.attachments);
      alert(`✅ ${files.length} file(s) uploaded!`);
    } catch { alert('❌ Upload failed.'); } finally { setUploading(false); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await taskAPI.delete(`/${taskId}/attachments/${id}`); setUploaded(prev => prev.filter(f => f._id !== id)); }
    catch { alert('❌ Delete failed.'); }
  };
  return (
    <div className="border border-purple-200 rounded-xl p-4 bg-purple-50 space-y-3 mt-4">
      <h4 className="font-semibold text-purple-700 text-sm flex items-center gap-2">
        <FaUpload /> Attachments <span className="text-xs font-normal text-gray-500">(visible to all assignees)</span>
      </h4>
      <div onClick={() => inputRef.current?.click()} onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-purple-300 rounded-lg p-4 text-center cursor-pointer hover:bg-purple-100 transition">
        <FaUpload className="mx-auto text-purple-400 text-xl mb-1" />
        <p className="text-sm text-gray-500">Click or drag & drop files here</p>
        <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, PDF, DOC, DOCX</p>
        <input ref={inputRef} type="file" multiple
          accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }} className="hidden" />
      </div>
      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase">Ready to upload</p>
          {pending.map((pf, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              {pf.preview ? <img src={pf.preview} alt="" className="w-9 h-9 object-cover rounded" /> : <FileIcon fileType={pf.fileType} />}
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-700 truncate">{pf.name}</p><p className="text-xs text-gray-400">{formatSize(pf.size)}</p></div>
              <button onClick={() => setPending(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><FaTrash /></button>
            </div>
          ))}
          <button onClick={handleUpload} disabled={uploading}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold py-2 rounded-lg flex items-center justify-center gap-2">
            {uploading ? <><FaSpinner className="animate-spin" /> Uploading...</> : <><FaUpload /> Upload {pending.length} file(s)</>}
          </button>
        </div>
      )}
      {uploaded.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase">Uploaded ({uploaded.length})</p>
          {uploaded.map(f => (
            <div key={f._id} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              <FileIcon fileType={f.fileType} />
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-700 truncate">{f.name}</p><p className="text-xs text-gray-400">by {f.uploadedBy} · {new Date(f.uploadedAt).toLocaleDateString('en-GB')}</p></div>
              <div className="flex items-center gap-2">
                {f.fileType === 'image' && <button onClick={() => setPreviewUrl(f.url)} className="text-blue-500 hover:text-blue-700"><FaEye /></button>}
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-700"><FaDownload /></a>
                <button onClick={() => handleDelete(f._id)} className="text-red-400 hover:text-red-600"><FaTrash /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!uploaded.length && !pending.length && <p className="text-xs text-gray-400 text-center">No attachments yet.</p>}
      {previewUrl && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-80 flex items-center justify-center px-4" onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-3xl w-full">
            <button onClick={() => setPreviewUrl(null)} className="absolute -top-10 right-0 text-white text-2xl">✕</button>
            <img src={previewUrl} alt="preview" className="w-full rounded-xl" />
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PLTaskManager — Project Leads can assign tasks to TL and Developer
// ─────────────────────────────────────────────────────────────────────────────
const PLTaskManager = () => {
  const worker = JSON.parse(localStorage.getItem('worker'));

  const [assignableWorkers, setAssignableWorkers]   = useState([]);
  const [myTasks, setMyTasks]                       = useState([]);
  const [selectedIds, setSelectedIds]               = useState([]);
  const [showForwardModal, setShowForwardModal]     = useState(false);
  const [forwardTaskId, setForwardTaskId]           = useState(null);
  const [forwardTargets, setForwardTargets]         = useState([]);
  const [forwardSelectedIds, setForwardSelectedIds] = useState([]);
  const [savedTaskId, setSavedTaskId]               = useState(null);
  const [savedTaskTitle, setSavedTaskTitle]         = useState('');
  const [showAttachModal, setShowAttachModal]       = useState(false);
  const [attachTask, setAttachTask]                 = useState(null);
  const [formData, setFormData] = useState({ title: '', deadline: '', description: '' });

  useEffect(() => { fetchAssignable(); fetchMyTasks(); }, []);

  const fetchAssignable = async () => {
    try { const res = await taskAPI.get('/assignable-workers'); setAssignableWorkers(res.data); }
    catch (err) { console.error(err); }
  };

  const fetchMyTasks = async () => {
    try { const res = await taskAPI.get('/created-by-me'); setMyTasks(res.data); }
    catch (err) { console.error(err); }
  };

  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.deadline || !formData.description) { alert('Fill all fields.'); return; }
    if (!selectedIds.length) { alert('Select at least one assignee.'); return; }
    try {
      const res = await taskAPI.post('/chain', { ...formData, assigneeIds: selectedIds });
      setSavedTaskId(res.data._id); setSavedTaskTitle(res.data.title);
      alert('✅ Task assigned! You can now upload attachments below.');
      setFormData({ title: '', deadline: '', description: '' }); setSelectedIds([]);
      fetchMyTasks();
    } catch (err) { alert(err.response?.data?.error || '❌ Failed to create task.'); }
  };

  const openForward = async (taskId) => {
    setForwardTaskId(taskId); setForwardSelectedIds([]);
    try { const res = await taskAPI.get('/assignable-workers'); setForwardTargets(res.data); }
    catch (err) { console.error(err); }
    setShowForwardModal(true);
  };

  const handleForward = async () => {
    if (!forwardSelectedIds.length) { alert('Select at least one worker.'); return; }
    try {
      await taskAPI.post(`/${forwardTaskId}/forward`, { assigneeIds: forwardSelectedIds });
      alert('✅ Forwarded!'); setShowForwardModal(false); fetchMyTasks();
    } catch (err) { alert(err.response?.data?.error || '❌ Forward failed.'); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 p-6 pt-20">
      <UserDashboard />
      <h2 className="text-3xl font-bold font-mono text-purple-700 mb-6 text-center mt-3">
        Project Leads — Task Manager
      </h2>
      <p className="text-center text-sm text-gray-500 mb-6">
        Assign tasks to <span className="font-semibold text-blue-600">TL</span> and <span className="font-semibold text-green-600">Developers</span>
      </p>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── CREATE FORM ── */}
        <div className="bg-white rounded-xl shadow-lg p-6 overflow-y-auto max-h-[85vh]">
          <h3 className="text-xl font-semibold text-purple-600 flex items-center gap-2 mb-4">
            <MdAssignment /> Create & Assign Task
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Task Title</label>
              <input type="text" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter task title" className="border rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-purple-400 outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1"><FaCalendarAlt className="inline mr-1" /> Deadline</label>
              <input type="date" value={formData.deadline} onChange={e => setFormData({ ...formData, deadline: e.target.value })}
                className="border rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-purple-400 outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1"><FaAlignLeft className="inline mr-1" /> Description</label>
              <textarea rows={3} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the task..." className="border rounded-lg px-4 py-2 w-full focus:ring-2 focus:ring-purple-400 outline-none" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-2">
                <MdGroups className="inline mr-1" /> Assign To — TL or Developer
              </label>
              <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                {assignableWorkers.length === 0 && <p className="text-sm text-gray-400 p-3">No assignable workers found.</p>}
                {assignableWorkers.map(w => (
                  <label key={w._id}
                    className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-purple-50 transition ${selectedIds.includes(w._id) ? 'bg-purple-50' : ''}`}>
                    <input type="checkbox" checked={selectedIds.includes(w._id)}
                      onChange={() => toggleSelect(w._id)} className="accent-purple-600" />
                    <span className="flex-1 text-sm font-medium text-gray-700">{w.username}</span>
                    <RoleBadge role={w.role} />
                    {w.team && <span className="text-xs text-gray-400">{w.team}</span>}
                  </label>
                ))}
              </div>
              {selectedIds.length > 0 && <p className="text-xs text-purple-600 mt-1">{selectedIds.length} selected</p>}
            </div>
            <button type="submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-lg transition">
              Assign Task
            </button>
          </form>

          {savedTaskId && (
            <div className="mt-2">
              <p className="text-xs text-green-600 font-medium mb-1">✅ Files for: <span className="font-bold">{savedTaskTitle}</span></p>
              <FileUploadPanel taskId={savedTaskId}
                existingFiles={myTasks.find(t => t._id === savedTaskId)?.attachments || []}
                onUploaded={(attachments) => setMyTasks(prev => prev.map(t => t._id === savedTaskId ? { ...t, attachments } : t))} />
            </div>
          )}
        </div>

        {/* ── TASK LIST ── */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-purple-600 mb-4 flex items-center gap-2">
            <FaLink /> Tasks I Created
          </h3>
          <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
            {myTasks.length === 0 && <p className="text-center text-gray-400 py-8">No tasks created yet.</p>}
            {myTasks.map(task => (
              <div key={task._id} className="border border-indigo-100 rounded-xl p-4 hover:shadow-md transition">
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <h4 className="font-semibold text-gray-800">{task.title}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">{task.description}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full shrink-0 ${
                    task.status === 'Completed' ? 'bg-green-100 text-green-700'
                    : task.status === 'In Progress' ? 'bg-blue-100 text-blue-700'
                    : 'bg-yellow-100 text-yellow-700'}`}>{task.status}</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Deadline: <span className="font-medium text-gray-600">
                    {task.deadline ? new Date(task.deadline).toLocaleDateString('en-GB') : '—'}
                  </span>
                </p>
                {task.assignmentChain?.length > 0 && (
                  <div className="mt-2"><p className="text-xs text-gray-400 mb-1">Chain:</p><ChainTrail chain={task.assignmentChain} /></div>
                )}
                {task.assignees?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-400 mb-1">Assigned to:</p>
                    <div className="flex flex-wrap gap-2">
                      {task.assignees.map((a, i) => (
                        <div key={i} className="flex items-center gap-1 bg-gray-50 border rounded-full px-3 py-1">
                          <span className="text-xs font-medium text-gray-700">{a.username}</span>
                          <RoleBadge role={a.role} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  {task.status !== 'Completed' && (
                    <button onClick={() => openForward(task._id)}
                      className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium">
                      <FaUserPlus /> Forward
                    </button>
                  )}
                  <button onClick={() => { setAttachTask(task); setShowAttachModal(true); }}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium">
                    <FaUpload /> {task.attachments?.length > 0 ? `${task.attachments.length} file(s)` : 'Add files'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Attachment modal */}
      {showAttachModal && attachTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowAttachModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Files</h3>
            <p className="text-sm text-gray-500 mb-4">"<span className="font-medium text-gray-700">{attachTask.title}</span>"</p>
            <FileUploadPanel taskId={attachTask._id} existingFiles={attachTask.attachments || []}
              onUploaded={(attachments) => {
                setMyTasks(prev => prev.map(t => t._id === attachTask._id ? { ...t, attachments } : t));
                setAttachTask(prev => ({ ...prev, attachments }));
              }} />
          </div>
        </div>
      )}

      {/* Forward modal */}
      {showForwardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 relative">
            <button onClick={() => setShowForwardModal(false)} className="absolute top-3 right-4 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-4">Forward Task</h3>
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto mb-4">
              {forwardTargets.map(w => (
                <label key={w._id}
                  className={`flex items-center gap-3 px-4 py-2 cursor-pointer hover:bg-purple-50 ${forwardSelectedIds.includes(w._id) ? 'bg-purple-50' : ''}`}>
                  <input type="checkbox" checked={forwardSelectedIds.includes(w._id)}
                    onChange={() => setForwardSelectedIds(prev => prev.includes(w._id) ? prev.filter(x => x !== w._id) : [...prev, w._id])}
                    className="accent-purple-600" />
                  <span className="flex-1 text-sm font-medium text-gray-700">{w.username}</span>
                  <RoleBadge role={w.role} />
                </label>
              ))}
            </div>
            <button onClick={handleForward} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-lg">Forward Task</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PLTaskManager;